import { ComponentPropsWithoutRef, useEffect, useMemo, useRef, useState } from 'react';
import NoData from '@/widget/NoData';
import { useTokenActivity } from '../../hook/useTokenActivity';
import { ITokenActivity } from '@/request/token/token.response.interface';
import ActivityHead from './components/ActivityHead';
import ActivityRow from './components/ActivityRow';
import ActivityLoading from './components/ActivityLoading';
import { useWebSocket } from '@/context/websocketContext';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useParams } from 'react-router-dom';
import { useTailwindRealHeight } from '@/hooks/base/useTailwindRealHeight';
import { useDynamicHeight } from '@/hooks/base/useDynamicHeight';

export type TradeType = 'buy' | 'sell' | 'all';
interface IProps extends Omit<ComponentPropsWithoutRef<'div'>, 'type'> {}
export default (_: IProps) => {
  const { data, isConnected } = useWebSocket();
  const [tradeType, setTradeType] = useState<TradeType>('all');
  const { address = '' } = useParams();

  const holdParams = useMemo(() => {
    return {
      tokenAddress: address,
      page: 1,
      limit: 100,
    };
  }, [address]);

  const { activityList, setActivityList, loading } = useTokenActivity(holdParams);
  const [renderList, setRenderList] = useState<ITokenActivity[]>([]);

  useEffect(() => {
    if (isConnected && data) {
      if (data.event == 'transactionUpdate') {
        if (data.data.txHash) {
          setActivityList((prev) => [data.data, ...prev]);
        }
      }
    }
  }, [isConnected, data]);

  useEffect(() => {
    if (tradeType == 'all') {
      setRenderList(activityList);
    } else {
      const list = activityList.filter((item) => item.type == tradeType);
      setRenderList(list);
    }
  }, [tradeType, activityList]);

  const parentRef = useRef<HTMLDivElement>(null);
  const tailwindRealHeight = useTailwindRealHeight(72);
  const viewHeight = useDynamicHeight({ customHeight: 100 });
  const rowVirtualizer = useVirtualizer({
    count: renderList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => tailwindRealHeight,
    overscan: 5,
  });

  return (
    <div>
      <ActivityHead tradeType={tradeType} setTradeType={setTradeType} />
      {loading ? (
        <ActivityLoading />
      ) : renderList.length > 0 ? (
        <div
          ref={parentRef}
          style={{
            height: viewHeight + 'px',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <div
                key={virtualRow.index}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: `${tailwindRealHeight}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <ActivityRow activity={renderList[virtualRow.index]} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <NoData />
      )}
    </div>
  );
};
